import { UnitedStatesState, builtEnvironment } from '@shared';
import { calculateTotalQuestions } from './utils';
import { nationwideCareMattersAZFormDefinition } from '@shared/application-form/nationwide/nationwideAppAZFormDef';
import { nationwideCareMattersCAFormDefinition } from '@shared/application-form/nationwide/nationwideAppCAFormDef';
import { nationwideCareMattersCTFormDefinition } from '@shared/application-form/nationwide/nationwideAppCTFormDef';
import { nationwideCareMattersDCFormDefinition } from '@shared/application-form/nationwide/nationwideAppDCFormDef';
import { nationwideCareMattersDEFormDefinition } from '@shared/application-form/nationwide/nationwideAppDEFormDef';
import { nationwideCareMattersFLFormDefinition } from '@shared/application-form/nationwide/nationwideAppFLFormDef';
import { nationwideCareMattersFormDefinition } from '@shared/application-form/nationwide/nationwideAppFormDef';
import { nationwideCareMattersINFormDefinition } from '@shared/application-form/nationwide/nationwideAppINFormDef';
import { nationwideCareMattersNDFormDefinition } from '@shared/application-form/nationwide/nationwideAppNDFormDef';
import { nationwideCareMattersNJFormDefinition } from '@shared/application-form/nationwide/nationwideAppNJFormDef';
import { nationwideCareMattersSDFormDefinition } from '@shared/application-form/nationwide/nationwideAppSDFormDef';
import { nationwideCareMattersSCFormDefinition } from '@shared/application-form/nationwide/nationwideAppSCFormDef';
import { oneAmericaAssetCareFormDef } from '@shared/application-form/one-america/oneAmericaAppFormDef';
import { oneAmericaAssetCareFLFormDef } from '@shared/application-form/one-america/oneAmericaAppFLFormDef';
import { oneAmericaAssetCareCAFormDef } from '@shared/application-form/one-america/oneAmericaAppCAFormDef';
import {
  careScoutFormDefinition,
  careScoutAZFormDefinition,
  careScoutCTFormDefinition,
  careScoutDCFormDefinition,
  careScoutDEFormDefinition,
  careScoutILFormDefinition,
  careScoutKSFormDefinition,
  careScoutMDFormDefinition,
  careScoutNDFormDefinition,
  careScoutORFormDefinition,
  careScoutSCFormDefinition,
  careScoutSDFormDefinition,
  careScoutTXFormDefinition,
  careScoutVTFormDefinition,
  careScoutWIFormDefinition,
} from '@shared/application-form/care-scout';

const carrierEmail = (email: string) =>
  builtEnvironment === 'prod' ? email : 'hello@joinwaterlily.com';

// =======================
// === APPLICATION FORM METADATA ===
// =======================
const assetCareMetaData: Omit<PolicyFormMetadata, 'formId' | 'templateId' | 'totalQuestions'> = {
  formType: 'application-form',
  title: 'Application Form',
  carrier: 'OneAmerica',
  carrierEmail: carrierEmail('NBApplicationSubmissions@oneamerica.com'),
  product: 'AssetCare',
  roleKeys: ['applicationPreparer'],
};

const nationwideMetaData: Omit<PolicyFormMetadata, 'formId' | 'templateId' | 'totalQuestions'> = {
  formType: 'application-form',
  title: 'Application Form',
  carrier: 'Nationwide',
  carrierEmail: carrierEmail('lifeapps@nationwide.com'),
  product: 'Care Matters II',
  roleKeys: [
    'applicationPreparer',
    'advisor',
    'primaryInsured',
    'policyOwner',
    'secondaryInsured',
    'jointOwner',
  ],
};

const careScoutMetaData: Omit<PolicyFormMetadata, 'formId' | 'templateId' | 'totalQuestions'> = {
  formType: 'application-form',
  title: 'Application Form',
  carrier: 'CareScout',
  // PROPER EMAIL NEEDS TO BE FILLED OUT
  carrierEmail: carrierEmail('hello+carescout@joinwaterlily.com'),
  product: 'Care Assurance',
  roleKeys: ['applicationPreparer'],
};

const createApplicationForm = (
  formId: string,
  templateId: string,
  formDef: PolicyFormDefinition,
  metaData: Omit<PolicyFormMetadata, 'formId' | 'templateId' | 'totalQuestions'>,
): PolicyFormMetadata => {
  return {
    formId,
    templateId,
    totalQuestions: calculateTotalQuestions(formDef) || 0,
    ...metaData,
  };
};

export const assetCareApplicationFormProd: Record<
  keyof typeof UnitedStatesState,
  PolicyFormMetadata
> = {
  default: createApplicationForm(
    '51029e82-d63d-491c-91ce-12a0270a6497',
    '51029e82-d63d-491c-91ce-12a0270a6497',
    oneAmericaAssetCareFormDef,
    assetCareMetaData,
  ),
  CA: createApplicationForm(
    'ad53e167-e891-4b32-8f6a-4fd13d6b7320',
    'ad53e167-e891-4b32-8f6a-4fd13d6b7320',
    oneAmericaAssetCareCAFormDef,
    assetCareMetaData,
  ),
  FL: createApplicationForm(
    '76c7e30d-e0d9-4244-90cc-3746e7fec1ad',
    '76c7e30d-e0d9-4244-90cc-3746e7fec1ad',
    oneAmericaAssetCareFLFormDef,
    assetCareMetaData,
  ),
};

export const assetCareApplicationFormDev: Record<
  keyof typeof UnitedStatesState,
  PolicyFormMetadata
> = {
  default: createApplicationForm(
    '26d2937f-fe88-4f7e-b554-de4ef20a7e43',
    '26d2937f-fe88-4f7e-b554-de4ef20a7e43',
    oneAmericaAssetCareFormDef,
    assetCareMetaData,
  ),
  CA: createApplicationForm(
    '1efd5f2c-5cda-4be3-a7f5-babb6a008318',
    '1efd5f2c-5cda-4be3-a7f5-babb6a008318',
    oneAmericaAssetCareCAFormDef,
    assetCareMetaData,
  ),
  FL: createApplicationForm(
    '1c080ff9-d10d-4123-a251-207a2ecc1766',
    '1c080ff9-d10d-4123-a251-207a2ecc1766',
    oneAmericaAssetCareFLFormDef,
    assetCareMetaData,
  ),
};

const nationwideApplicationFormProd: Record<keyof typeof UnitedStatesState, PolicyFormMetadata> = {
  default: createApplicationForm(
    '1d5a09c1-df5e-43d8-bf53-859fe4e72e90',
    '1d5a09c1-df5e-43d8-bf53-859fe4e72e90',
    nationwideCareMattersFormDefinition,
    nationwideMetaData,
  ),
  AZ: createApplicationForm(
    'b69921b5-ae1e-43f9-869a-9f0e23a31da7',
    'b69921b5-ae1e-43f9-869a-9f0e23a31da7',
    nationwideCareMattersAZFormDefinition,
    nationwideMetaData,
  ),
  CA: createApplicationForm(
    '9faeba90-22bc-48bc-8618-e40225d6a9ce',
    '9faeba90-22bc-48bc-8618-e40225d6a9ce',
    nationwideCareMattersCAFormDefinition,
    nationwideMetaData,
  ),
  CT: createApplicationForm(
    'ad6546e9-0eb6-4148-8801-cf16e917b1c3',
    'ad6546e9-0eb6-4148-8801-cf16e917b1c3',
    nationwideCareMattersCTFormDefinition,
    nationwideMetaData,
  ),
  DC: createApplicationForm(
    '4fe94f26-8c4e-4ce2-bd65-4846dc9b23d0',
    '4fe94f26-8c4e-4ce2-bd65-4846dc9b23d0',
    nationwideCareMattersDCFormDefinition,
    nationwideMetaData,
  ),
  DE: createApplicationForm(
    '3aa2da7d-6c7e-4190-b375-48eddf0b6851',
    '3aa2da7d-6c7e-4190-b375-48eddf0b6851',
    nationwideCareMattersDEFormDefinition,
    nationwideMetaData,
  ),
  FL: createApplicationForm(
    'eda1593d-072c-41f0-91d6-380cebfdf97e',
    'eda1593d-072c-41f0-91d6-380cebfdf97e',
    nationwideCareMattersFLFormDefinition,
    nationwideMetaData,
  ),
  IN: createApplicationForm(
    '41d63c82-6fdd-4bb8-b400-528c0b7d95f3',
    '41d63c82-6fdd-4bb8-b400-528c0b7d95f3',
    nationwideCareMattersINFormDefinition,
    nationwideMetaData,
  ),
  ND: createApplicationForm(
    '873c11b0-954d-40bf-9401-01be51bd9142',
    '873c11b0-954d-40bf-9401-01be51bd9142',
    nationwideCareMattersNDFormDefinition,
    nationwideMetaData,
  ),
  NJ: createApplicationForm(
    'aa359c98-da72-4b3b-b1a4-b510e7307d87',
    'aa359c98-da72-4b3b-b1a4-b510e7307d87',
    nationwideCareMattersNJFormDefinition,
    nationwideMetaData,
  ),
  SC: createApplicationForm(
    '11196104-2c80-40a8-ad3a-1af8f7047f20',
    '11196104-2c80-40a8-ad3a-1af8f7047f20',
    nationwideCareMattersSCFormDefinition,
    nationwideMetaData,
  ),
  SD: createApplicationForm(
    'c5a6c3fd-dacd-4c73-8138-589d6eb2e0b1',
    'c5a6c3fd-dacd-4c73-8138-589d6eb2e0b1',
    nationwideCareMattersSDFormDefinition,
    nationwideMetaData,
  ),
};

const nationwideApplicationFormDev: Record<keyof typeof UnitedStatesState, PolicyFormMetadata> = {
  default: createApplicationForm(
    '1b2d0e8e-6e40-48fd-a790-193740020400',
    '1b2d0e8e-6e40-48fd-a790-193740020400',
    nationwideCareMattersFormDefinition,
    nationwideMetaData,
  ),
  AZ: createApplicationForm(
    '77bd1c69-e412-4184-b751-dfb21fbded38',
    '77bd1c69-e412-4184-b751-dfb21fbded38',
    nationwideCareMattersAZFormDefinition,
    nationwideMetaData,
  ),
  CA: createApplicationForm(
    '7af6bb75-f618-4b22-9823-a09a5784ab1d',
    '7af6bb75-f618-4b22-9823-a09a5784ab1d',
    nationwideCareMattersCAFormDefinition,
    nationwideMetaData,
  ),
  CT: createApplicationForm(
    '92c62d33-382b-45c2-898d-f7184848734c',
    '92c62d33-382b-45c2-898d-f7184848734c',
    nationwideCareMattersCTFormDefinition,
    nationwideMetaData,
  ),
  DC: createApplicationForm(
    '8f793836-114f-45dc-bc95-cb76375a4c04',
    '8f793836-114f-45dc-bc95-cb76375a4c04',
    nationwideCareMattersDCFormDefinition,
    nationwideMetaData,
  ),
  DE: createApplicationForm(
    'd4b959f5-ef43-4c98-b266-736a94dcf2d2',
    'd4b959f5-ef43-4c98-b266-736a94dcf2d2',
    nationwideCareMattersDEFormDefinition,
    nationwideMetaData,
  ),
  FL: createApplicationForm(
    'eb4e6fc7-6bde-439b-b404-74289b60a261',
    'eb4e6fc7-6bde-439b-b404-74289b60a261',
    nationwideCareMattersFLFormDefinition,
    nationwideMetaData,
  ),
  IN: createApplicationForm(
    '941b5e0f-3cda-4757-abbd-879dd78f6ea8',
    '941b5e0f-3cda-4757-abbd-879dd78f6ea8',
    nationwideCareMattersINFormDefinition,
    nationwideMetaData,
  ),
  ND: createApplicationForm(
    '9f4a9de8-6a62-4325-ac09-28b95518a0aa',
    '9f4a9de8-6a62-4325-ac09-28b95518a0aa',
    nationwideCareMattersNDFormDefinition,
    nationwideMetaData,
  ),
  NJ: createApplicationForm(
    '93d62724-bd2f-48a0-bf4a-3eaf9d904cd9',
    '93d62724-bd2f-48a0-bf4a-3eaf9d904cd9',
    nationwideCareMattersNJFormDefinition,
    nationwideMetaData,
  ),
  SC: createApplicationForm(
    'bf20f6bc-da46-4694-996d-e02e98c59630',
    'bf20f6bc-da46-4694-996d-e02e98c59630',
    nationwideCareMattersSCFormDefinition,
    nationwideMetaData,
  ),
  SD: createApplicationForm(
    '1b52c826-4f74-4272-af08-3cc7792122b2',
    '1b52c826-4f74-4272-af08-3cc7792122b2',
    nationwideCareMattersSDFormDefinition,
    nationwideMetaData,
  ),
};

export const careScoutApplicationFormProd: Record<
  keyof typeof UnitedStatesState,
  PolicyFormMetadata
> = {
  default: createApplicationForm(
    '6663af8d-29db-4078-8f76-767af93d2f7e',
    '6663af8d-29db-4078-8f76-767af93d2f7e',
    careScoutFormDefinition,
    careScoutMetaData,
  ),
  AZ: createApplicationForm(
    'a04b5991-853b-4d28-8efb-9e9c6a0bb62f',
    'a04b5991-853b-4d28-8efb-9e9c6a0bb62f',
    careScoutAZFormDefinition,
    careScoutMetaData,
  ),
  CT: createApplicationForm(
    'fdddecb2-17ce-4ce9-aaee-10be2008eec3',
    'fdddecb2-17ce-4ce9-aaee-10be2008eec3',
    careScoutCTFormDefinition,
    careScoutMetaData,
  ),
  DC: createApplicationForm(
    '17fff9b9-cb16-47c2-ac28-80b3379d3669',
    '17fff9b9-cb16-47c2-ac28-80b3379d3669',
    careScoutDCFormDefinition,
    careScoutMetaData,
  ),
  DE: createApplicationForm(
    'c6324391-305c-472e-9fd2-2820d8925b6d',
    'c6324391-305c-472e-9fd2-2820d8925b6d',
    careScoutDEFormDefinition,
    careScoutMetaData,
  ),
  IL: createApplicationForm(
    '98cfd0db-4a89-4c91-ba70-4e432fbb4afc',
    '98cfd0db-4a89-4c91-ba70-4e432fbb4afc',
    careScoutILFormDefinition,
    careScoutMetaData,
  ),
  KS: createApplicationForm(
    'c84e6207-42c2-42a5-94f4-8db2c16ef8a6',
    'c84e6207-42c2-42a5-94f4-8db2c16ef8a6',
    careScoutKSFormDefinition,
    careScoutMetaData,
  ),
  MD: createApplicationForm(
    'f3309549-96a7-4fae-a30b-7209bea51eb7',
    'f3309549-96a7-4fae-a30b-7209bea51eb7',
    careScoutMDFormDefinition,
    careScoutMetaData,
  ),
  ND: createApplicationForm(
    '9ef7cbfd-584a-4b9b-96a0-e649d67b0774',
    '9ef7cbfd-584a-4b9b-96a0-e649d67b0774',
    careScoutNDFormDefinition,
    careScoutMetaData,
  ),
  OR: createApplicationForm(
    '07d796b0-6e0d-4cb7-9a5c-4e3927556ae2',
    '07d796b0-6e0d-4cb7-9a5c-4e3927556ae2',
    careScoutORFormDefinition,
    careScoutMetaData,
  ),
  SC: createApplicationForm(
    '077d2df2-c893-4da4-85bd-5da31dacc2fb',
    '077d2df2-c893-4da4-85bd-5da31dacc2fb',
    careScoutSCFormDefinition,
    careScoutMetaData,
  ),
  SD: createApplicationForm(
    'f7596852-5745-428d-b28a-4c65591081d6',
    'f7596852-5745-428d-b28a-4c65591081d6',
    careScoutSDFormDefinition,
    careScoutMetaData,
  ),
  TX: createApplicationForm(
    'e4b467b4-0168-45c9-9e66-189483144fc5',
    'e4b467b4-0168-45c9-9e66-189483144fc5',
    careScoutTXFormDefinition,
    careScoutMetaData,
  ),
  VT: createApplicationForm(
    '5c95dd48-7696-4df3-b733-1ab14e386713',
    '5c95dd48-7696-4df3-b733-1ab14e386713',
    careScoutVTFormDefinition,
    careScoutMetaData,
  ),
  WI: createApplicationForm(
    '0839ea52-8d45-40ee-b3b6-244109067cb0',
    '0839ea52-8d45-40ee-b3b6-244109067cb0',
    careScoutWIFormDefinition,
    careScoutMetaData,
  ),
};

export const careScoutApplicationFormDev: Record<
  keyof typeof UnitedStatesState,
  PolicyFormMetadata
> = {
  default: createApplicationForm(
    '100b8c65-85af-429f-a0b9-932ca624a99d',
    '100b8c65-85af-429f-a0b9-932ca624a99d',
    careScoutFormDefinition,
    careScoutMetaData,
  ),
  AZ: createApplicationForm(
    'e9977793-135e-4ead-9637-1877a6f70f3b',
    'e9977793-135e-4ead-9637-1877a6f70f3b',
    careScoutAZFormDefinition,
    careScoutMetaData,
  ),
  CT: createApplicationForm(
    '08ba9574-4cb0-4990-9dcc-f4fe51695416',
    '08ba9574-4cb0-4990-9dcc-f4fe51695416',
    careScoutCTFormDefinition,
    careScoutMetaData,
  ),
  DC: createApplicationForm(
    '8df1206c-29aa-49ef-a336-a2c3b91fd5f1',
    '8df1206c-29aa-49ef-a336-a2c3b91fd5f1',
    careScoutDCFormDefinition,
    careScoutMetaData,
  ),
  DE: createApplicationForm(
    '451cb95f-4785-4c80-8afc-186a2f3217cd',
    '451cb95f-4785-4c80-8afc-186a2f3217cd',
    careScoutDEFormDefinition,
    careScoutMetaData,
  ),
  IL: createApplicationForm(
    '5ef65a0c-d31f-4137-8bc0-41da6aadb8b7',
    '5ef65a0c-d31f-4137-8bc0-41da6aadb8b7',
    careScoutILFormDefinition,
    careScoutMetaData,
  ),
  KS: createApplicationForm(
    '08587491-1376-47aa-8b73-77e93a8fedec',
    '08587491-1376-47aa-8b73-77e93a8fedec',
    careScoutKSFormDefinition,
    careScoutMetaData,
  ),
  MD: createApplicationForm(
    '16d30f44-ada3-4a5f-bb35-d5594881f2ce',
    '16d30f44-ada3-4a5f-bb35-d5594881f2ce',
    careScoutMDFormDefinition,
    careScoutMetaData,
  ),
  ND: createApplicationForm(
    '4604f07b-49c8-4246-a4ed-c9c51fb6a20f',
    '4604f07b-49c8-4246-a4ed-c9c51fb6a20f',
    careScoutNDFormDefinition,
    careScoutMetaData,
  ),
  OR: createApplicationForm(
    '24f311c2-7464-41ea-aa71-78f1a5e46593',
    '24f311c2-7464-41ea-aa71-78f1a5e46593',
    careScoutORFormDefinition,
    careScoutMetaData,
  ),
  SD: createApplicationForm(
    'd525e82d-79dd-44a8-93c3-075ed9fddb36',
    'd525e82d-79dd-44a8-93c3-075ed9fddb36',
    careScoutSDFormDefinition,
    careScoutMetaData,
  ),
  SC: createApplicationForm(
    '4dc57675-81b4-4c7a-b41e-29808e73c729',
    '4dc57675-81b4-4c7a-b41e-29808e73c729',
    careScoutSCFormDefinition,
    careScoutMetaData,
  ),
  TX: createApplicationForm(
    '2bcfe229-e74e-4a88-9633-b537fe66b442',
    '2bcfe229-e74e-4a88-9633-b537fe66b442',
    careScoutTXFormDefinition,
    careScoutMetaData,
  ),
  VT: createApplicationForm(
    'ec0b3fc6-08ee-4a2f-8f19-60dca730fbff',
    'ec0b3fc6-08ee-4a2f-8f19-60dca730fbff',
    careScoutVTFormDefinition,
    careScoutMetaData,
  ),
  WI: createApplicationForm(
    'a5e8882a-f8aa-4e25-8d40-2eceba9af92a',
    'a5e8882a-f8aa-4e25-8d40-2eceba9af92a',
    careScoutWIFormDefinition,
    careScoutMetaData,
  ),
};

export const nationwideApplicationForm =
  builtEnvironment === 'prod' ? nationwideApplicationFormProd : nationwideApplicationFormDev;

export const assetCareApplicationForm =
  builtEnvironment === 'prod' ? assetCareApplicationFormProd : assetCareApplicationFormDev;

export const careScoutApplicationForm =
  builtEnvironment === 'prod' ? careScoutApplicationFormProd : careScoutApplicationFormDev;
